import { useState, useEffect } from "react";
import {
  VStack,
  Input,
  Textarea,
  Button,
  Heading,
  Text,
  NumberInput,
  NumberInputField,
  FormLabel,
  FormControl,
  FormHelperText,
  Box,
  useToast,
  Progress,
  HStack,
  Checkbox,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { AttachmentIcon } from "@chakra-ui/icons";
import styled from "styled-components";
import { DragDrop, LogoFile } from "../DragDrop";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import axios from "axios";
import { rootUrl } from "../../api/rootUrl";
import {
  Campaign,
  createCampaign,
  CreateCampaignRequest,
} from "../../api/admin/adminCampaign";
import { ImageAssetPicker } from "./ImageAssetPicker";
import { EinSearchBar } from "../EINSearchBar";
import { EINData } from "../../api/clientUser/getEinLookup";
import { useRef } from "react";

const WizardContainer = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StepContainer = styled.div`
  min-height: 400px;

  @media (max-width: 768px) {
    min-height: 300px;
  }
`;

const ImagePreview = styled.img<{ $square?: boolean }>`
  width: ${(props) => (props.$square ? "100px" : "200px")};
  height: ${(props) => (props.$square ? "100px" : "auto")};
  max-height: ${(props) => (props.$square ? "100px" : "150px")};
  object-fit: cover;
  border-radius: 8px;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    width: ${(props) => (props.$square ? "80px" : "150px")};
    height: ${(props) => (props.$square ? "80px" : "auto")};
    max-height: ${(props) => (props.$square ? "80px" : "120px")};
  }
`;

const Section = styled.div`
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

interface Props {
  selectedClientId: string;
  isIndividualClient?: boolean;
  onCampaignCreated: (campaign: Campaign) => void;
  aiSuggestions?: any;
  onSuggestionsApplied?: () => void;
}

export const CampaignWizard = ({
  selectedClientId,
  isIndividualClient = false,
  onCampaignCreated,
  aiSuggestions,
  onSuggestionsApplied,
}: Props) => {
  const { adminToken } = useAdminAuth();
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const cancelRef = useRef(null);

  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const {
    isOpen: isPickerOpen,
    onOpen: onPickerOpen,
    onClose: onPickerClose,
  } = useDisclosure();
  const [currentImageType, setCurrentImageType] = useState<
    "logo" | "alertImage"
  >("logo");

  // Step 0 (individuals only): Nonprofit EIN
  const [ein, setEin] = useState("");
  const [nonprofitName, setNonprofitName] = useState("");

  // Step 1: Basic Info
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState("");
  const [goalAmount, setGoalAmount] = useState("0");

  // Step 2: Timing & Urgency
  const [expires, setExpires] = useState("");
  const [tagline, setTagline] = useState("");

  // Step 3: Branding
  const [colorTheme, setColorTheme] = useState("#F4B942");

  // Step 4: Optional Features
  const [enableAlertBanner, setEnableAlertBanner] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [alertImage, setAlertImage] = useState("");
  const [alertButtonLabel, setAlertButtonLabel] = useState("");

  const [enableMatchingDonors, setEnableMatchingDonors] = useState(false);
  const [matchPoolAmount, setMatchPoolAmount] = useState("0");

  // Apply AI suggestions
  useEffect(() => {
    if (aiSuggestions && Object.keys(aiSuggestions).length > 0) {
      if (aiSuggestions.name) setName(aiSuggestions.name);
      if (aiSuggestions.description) setDescription(aiSuggestions.description);
      if (aiSuggestions.goalAmount) setGoalAmount(aiSuggestions.goalAmount);
      if (aiSuggestions.tagline) setTagline(aiSuggestions.tagline);
      if (aiSuggestions.expires) setExpires(aiSuggestions.expires);

      toast({
        title: "AI suggestions applied! ✨",
        description: "Review and adjust the details in the wizard",
        status: "success",
        duration: 3000,
      });

      onSuggestionsApplied?.();
    }
  }, [aiSuggestions, toast, onSuggestionsApplied]);

  const handleImageUpload = async (
    file: LogoFile,
    type: "logo" | "alertImage"
  ) => {
    if (!adminToken || !selectedClientId) return;
    try {
      const fileType = type === "logo" ? "CAMPAIGN_LOGO" : "ALERT_IMAGE";

      const result = await axios.post(
        `${rootUrl}/admin/upload`,
        {
          file: file.base64,
          type: fileType,
          mimeType: file.type,
          name: file.name,
          clientId: selectedClientId,
        },
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );

      const imageUrl = result.data.url;

      if (type === "logo") setLogo(imageUrl);
      else setAlertImage(imageUrl);

      toast({
        title: "Image uploaded successfully",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Failed to upload image",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleOpenImagePicker = (type: "logo" | "alertImage") => {
    setCurrentImageType(type);
    onPickerOpen();
  };

  const handleSelectFromLibrary = (url: string) => {
    if (currentImageType === "logo") setLogo(url);
    else setAlertImage(url);
  };

  const canProceedFromStep = (step: number): boolean => {
    if (isIndividualClient && step === 1) {
      return !!ein;
    }
    const basicInfoStep = isIndividualClient ? 2 : 1;
    if (step === basicInfoStep) {
      return !!(name && description && logo && parseFloat(goalAmount) > 0);
    }
    return true;
  };

  const handleNext = () => {
    if (!canProceedFromStep(currentStep)) {
      toast({
        title: "Please complete required fields",
        status: "warning",
        duration: 2000,
      });
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleCreate = async () => {
    if (!adminToken || !selectedClientId) return;

    onConfirmClose();
    setIsSaving(true);

    try {
      // Convert local datetime to UTC ISO string
      const expiresUTC = expires ? new Date(expires).toISOString() : undefined;

      const data: CreateCampaignRequest = {
        clientId: selectedClientId,
        ein: ein || undefined,
        beneficiaryName: nonprofitName || undefined,
        name,
        description,
        logo,
        goalAmount: Math.round(parseFloat(goalAmount) * 100),
        expires: expiresUTC,
        tagline: tagline || undefined,
        colorTheme: colorTheme || undefined,
        alertTitle: enableAlertBanner ? alertTitle || undefined : undefined,
        alertDescription: enableAlertBanner
          ? alertDescription || undefined
          : undefined,
        alertImage: enableAlertBanner ? alertImage || undefined : undefined,
        alertButtonLabel: enableAlertBanner
          ? alertButtonLabel || undefined
          : undefined,
        sponsorBonusMultiple: enableMatchingDonors ? 100 : undefined, // 1x match (atomic: 100 = 1.00)
        maxBonus: enableMatchingDonors
          ? Math.round(parseFloat(matchPoolAmount) * 100)
          : undefined,
      };

      const result = await createCampaign(adminToken, data);

      toast({
        title: "Campaign created! 🎉",
        description: "Now let's schedule messages",
        status: "success",
        duration: 2000,
      });

      onCampaignCreated(result);
    } catch (error) {
      toast({
        title: "Failed to create campaign",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const totalSteps = isIndividualClient ? 6 : 5;
  const progressPercent = (currentStep / totalSteps) * 100;

  const handleEinSelect = (result: EINData) => {
    setEin(result.ein);
    setNonprofitName(result.name);
  };

  const renderStep = () => {
    // For individual clients, step 1 is the EIN selection
    if (isIndividualClient && currentStep === 1) {
      return (
        <StepContainer>
          <Heading size={{ base: "sm", md: "md" }} mb={4}>
            Step 1: Select Nonprofit
          </Heading>
          <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" mb={6}>
            Which 501(c)(3) nonprofit is this campaign raising funds for?
          </Text>

          <Section>
            <FormControl isRequired>
              <FormLabel>Search by EIN or Organization Name</FormLabel>
              <EinSearchBar onSelectResult={handleEinSelect} />
              {ein && (
                <Box p={3} bg="green.50" borderRadius="md" mt={3}>
                  <Text fontSize="sm" fontWeight="600" color="green.700">
                    Selected: {nonprofitName} (EIN: {ein})
                  </Text>
                </Box>
              )}
            </FormControl>
          </Section>
        </StepContainer>
      );
    }

    // Offset step number for individuals (their step 2 = org's step 1)
    const effectiveStep = isIndividualClient ? currentStep - 1 : currentStep;

    switch (effectiveStep) {
      case 1:
        return (
          <StepContainer>
            <Heading size={{ base: "sm", md: "md" }} mb={4}>
              Step {isIndividualClient ? 2 : 1}: Basic Information
            </Heading>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" mb={6}>
              Let's start with the essential details about your campaign.
            </Text>

            <Section>
              <FormControl isRequired>
                <FormLabel>Campaign Name</FormLabel>
                <FormHelperText mb={2}>
                  Appears in SMS messages and on the campaign donation page
                </FormHelperText>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Annual Gala 2025"
                />
              </FormControl>
            </Section>

            <Section>
              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <FormHelperText mb={2}>
                  Tell donors about your cause and its impact (2-3 sentences)
                </FormHelperText>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Help us raise funds for..."
                  rows={3}
                />
              </FormControl>
            </Section>

            <Section>
              <FormControl isRequired>
                <FormLabel>Campaign Icon (Square 1:1)</FormLabel>
                <FormHelperText mb={2}>
                  Square image displayed in messages and page header
                </FormHelperText>
                <Button
                  onClick={() => handleOpenImagePicker("logo")}
                  size="sm"
                  variant="outline"
                  leftIcon={<AttachmentIcon />}
                  mb={2}
                >
                  Select from Library
                </Button>
                <DragDrop
                  onLogoChange={(file) => handleImageUpload(file, "logo")}
                  enforceSquare={true}
                  label="Upload or drop square campaign icon"
                />
                {logo && (
                  <ImagePreview src={logo} alt="Campaign icon" $square />
                )}
              </FormControl>
            </Section>

            <Section>
              <FormControl isRequired>
                <FormLabel>Fundraising Goal ($)</FormLabel>
                <FormHelperText mb={2}>
                  Your fundraising target in USD
                </FormHelperText>
                <NumberInput
                  value={goalAmount}
                  onChange={setGoalAmount}
                  min={0}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            </Section>
          </StepContainer>
        );

      case 2:
        return (
          <StepContainer>
            <Heading size={{ base: "sm", md: "md" }} mb={4}>
              Step {isIndividualClient ? 3 : 2}: Timing & Urgency
            </Heading>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" mb={6}>
              Add deadline details to create urgency for donors.
            </Text>

            <Section>
              <FormControl>
                <FormLabel>Expiration Date & Time (Local)</FormLabel>
                <FormHelperText mb={2}>
                  When campaign ends - enables countdown timer on donation page
                </FormHelperText>
                <Input
                  type="datetime-local"
                  value={expires}
                  onChange={(e) => setExpires(e.target.value)}
                />
              </FormControl>
            </Section>


            <Section>
              <FormControl>
                <FormLabel>Tagline (Optional)</FormLabel>
                <FormHelperText mb={2}>
                  Short, 2-5 words displayed as a small pill on the campaign
                  page
                </FormHelperText>
                <Input
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="Every gift makes a difference"
                />
              </FormControl>
            </Section>
          </StepContainer>
        );

      case 3:
        return (
          <StepContainer>
            <Heading size={{ base: "sm", md: "md" }} mb={4}>
              Step {isIndividualClient ? 4 : 3}: Branding
            </Heading>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" mb={6}>
              Customize the look and feel of your campaign page.
            </Text>

            <Section>
              <FormControl>
                <FormLabel>Color Theme</FormLabel>
                <FormHelperText mb={2}>
                  Primary color for campaign page and donation buttons
                </FormHelperText>
                <Input
                  type="color"
                  value={colorTheme}
                  onChange={(e) => setColorTheme(e.target.value)}
                />
              </FormControl>
            </Section>

            <Box p={4} bg="gray.50" borderRadius="md" mt={4}>
              <Text fontSize="sm" fontWeight="600" mb={2}>
                Preview:
              </Text>
              <Box
                p={3}
                bg={colorTheme}
                color="white"
                borderRadius="md"
                textAlign="center"
              >
                Sample Donate Button
              </Box>
            </Box>
          </StepContainer>
        );

      case 4:
        return (
          <StepContainer>
            <Heading size={{ base: "sm", md: "md" }} mb={4}>
              Step {isIndividualClient ? 5 : 4}: Optional Features
            </Heading>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" mb={6}>
              Add extra features to enhance your campaign.
            </Text>

            <Section>
              <Checkbox
                isChecked={enableAlertBanner}
                onChange={(e) => setEnableAlertBanner(e.target.checked)}
                size="lg"
                mb={3}
              >
                <Text fontWeight="600">Enable Alert Banner</Text>
              </Checkbox>
              <Text fontSize="sm" color="gray.600" ml={6} mb={4}>
                Promotional banner at top of page (e.g., "Matching gifts
                available!")
              </Text>

              {enableAlertBanner && (
                <Box
                  pl={6}
                  borderLeft="3px solid"
                  borderColor="blue.200"
                  ml={3}
                >
                  <Section>
                    <FormControl>
                      <FormLabel>Alert Title</FormLabel>
                      <Input
                        value={alertTitle}
                        onChange={(e) => setAlertTitle(e.target.value)}
                        placeholder="🔥 Matching gifts available!"
                      />
                    </FormControl>
                  </Section>

                  <Section>
                    <FormControl>
                      <FormLabel>Alert Description</FormLabel>
                      <Textarea
                        value={alertDescription}
                        onChange={(e) => setAlertDescription(e.target.value)}
                        placeholder="All donations matched 2x until midnight"
                        rows={2}
                      />
                    </FormControl>
                  </Section>

                  <Section>
                    <FormControl>
                      <FormLabel>Alert Image</FormLabel>
                      <Button
                        onClick={() => handleOpenImagePicker("alertImage")}
                        size="sm"
                        variant="outline"
                        leftIcon={<AttachmentIcon />}
                        mb={2}
                      >
                        Select from Library
                      </Button>
                      <DragDrop
                        onLogoChange={(file) =>
                          handleImageUpload(file, "alertImage")
                        }
                        label="Upload or drop alert banner image"
                      />
                      {alertImage && (
                        <ImagePreview src={alertImage} alt="Alert image" />
                      )}
                    </FormControl>
                  </Section>

                  <Section>
                    <FormControl>
                      <FormLabel>Alert Button Label</FormLabel>
                      <Input
                        value={alertButtonLabel}
                        onChange={(e) => setAlertButtonLabel(e.target.value)}
                        placeholder="Donate Now"
                      />
                    </FormControl>
                  </Section>
                </Box>
              )}
            </Section>

            <Box mt={6}>
              <Checkbox
                isChecked={enableMatchingDonors}
                onChange={(e) => setEnableMatchingDonors(e.target.checked)}
                size="lg"
                mb={3}
              >
                <Text fontWeight="600">This campaign has matching donors</Text>
              </Checkbox>
              <Text fontSize="sm" color="gray.600" ml={6} mb={4}>
                Enable if sponsors will match donations 1:1 until pool is
                exhausted
              </Text>

              {enableMatchingDonors && (
                <Box
                  pl={6}
                  borderLeft="3px solid"
                  borderColor="green.200"
                  ml={3}
                >
                  <Section>
                    <FormControl>
                      <FormLabel>Total Match Pool (USD)</FormLabel>
                      <FormHelperText mb={2}>
                        All donations matched 1:1 until this amount is reached
                      </FormHelperText>
                      <NumberInput
                        value={matchPoolAmount}
                        onChange={setMatchPoolAmount}
                        min={0}
                      >
                        <NumberInputField placeholder="5000" />
                      </NumberInput>
                    </FormControl>
                  </Section>

                  {parseFloat(matchPoolAmount) > 0 && (
                    <Box p={3} bg="green.50" borderRadius="md" mt={3}>
                      <Text fontSize="sm" fontWeight="600" color="green.700">
                        💚 Matching Preview: Sponsors will match donations 1:1
                        up to ${parseFloat(matchPoolAmount).toLocaleString()}{" "}
                        total
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </StepContainer>
        );

      case 5:
        return (
          <StepContainer>
            <Heading size={{ base: "sm", md: "md" }} mb={4}>
              Step {isIndividualClient ? 6 : 5}: Review & Create
            </Heading>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" mb={6}>
              Review your campaign details before creating.
            </Text>

            <Box p={4} bg="gray.50" borderRadius="md">
              <VStack align="stretch" spacing={3}>
                {isIndividualClient && ein && (
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="600">
                      RAISING FOR
                    </Text>
                    <Text>{nonprofitName} (EIN: {ein})</Text>
                  </Box>
                )}
                <Box>
                  <Text fontSize="xs" color="gray.600" fontWeight="600">
                    CAMPAIGN NAME
                  </Text>
                  <Text>{name}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.600" fontWeight="600">
                    DESCRIPTION
                  </Text>
                  <Text fontSize="sm">{description}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.600" fontWeight="600">
                    GOAL
                  </Text>
                  <Text>${parseFloat(goalAmount).toLocaleString()}</Text>
                </Box>
                {expires && (
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="600">
                      EXPIRES
                    </Text>
                    <Text>{new Date(expires).toLocaleString()}</Text>
                  </Box>
                )}
                {tagline && (
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="600">
                      TAGLINE
                    </Text>
                    <Text>{tagline}</Text>
                  </Box>
                )}
                {enableAlertBanner && alertTitle && (
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="600">
                      ALERT BANNER
                    </Text>
                    <Text>{alertTitle}</Text>
                  </Box>
                )}
                {enableMatchingDonors && parseFloat(matchPoolAmount) > 0 && (
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="600">
                      MATCHING DONORS
                    </Text>
                    <Text>
                      Match pool: $
                      {parseFloat(matchPoolAmount).toLocaleString()}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>

            <Box p={4} bg="blue.50" borderRadius="md" mt={4}>
              <Text fontSize="sm" fontWeight="600" color="blue.700">
                ℹ️ Next Step: After creating, you'll schedule text messages for
                your campaign.
              </Text>
            </Box>
          </StepContainer>
        );

      default:
        return null;
    }
  };

  return (
    <WizardContainer>
      <VStack spacing={6} align="stretch">
        {/* Progress Bar */}
        <Box>
          <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="600" color="gray.600">
              Step {currentStep} of {totalSteps}
            </Text>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
              {Math.round(progressPercent)}% Complete
            </Text>
          </HStack>
          <Progress
            value={progressPercent}
            colorScheme="blue"
            borderRadius="md"
            height={{ base: "6px", md: "8px" }}
          />
        </Box>

        {/* Current Step */}
        {renderStep()}

        {/* Navigation Buttons */}
        <HStack
          spacing={3}
          justify="space-between"
          flexDirection={{ base: "column-reverse", md: "row" }}
          width="100%"
        >
          <Button
            onClick={handleBack}
            variant="outline"
            isDisabled={currentStep === 1}
            width={{ base: "100%", md: "auto" }}
          >
            ← Back
          </Button>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              colorScheme="blue"
              isDisabled={!canProceedFromStep(currentStep)}
              width={{ base: "100%", md: "auto" }}
            >
              Next →
            </Button>
          ) : (
            <Button
              onClick={onConfirmOpen}
              colorScheme="green"
              size="lg"
              isDisabled={!(name && description && logo && parseFloat(goalAmount) > 0 && (!isIndividualClient || ein))}
              width={{ base: "100%", md: "auto" }}
            >
              Create Campaign & Schedule Messages
            </Button>
          )}
        </HStack>
      </VStack>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Create Campaign?
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={3}>
                This will create your campaign and take you to message
                scheduling.
              </Text>
              <Text fontSize="sm" color="gray.600">
                You can edit campaign details anytime after creation.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={handleCreate}
                ml={3}
                isLoading={isSaving}
              >
                Create Campaign
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Image Asset Picker */}
      <ImageAssetPicker
        isOpen={isPickerOpen}
        onClose={onPickerClose}
        clientId={selectedClientId}
        onSelectImage={handleSelectFromLibrary}
      />
    </WizardContainer>
  );
};
